import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CalendarBlank, Lightning, LockSimple, Phone, Buildings, Clock, MapPin,
  CheckCircle, CaretLeft, CaretRight, ListBullets, Info,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AvailabilityEditor from '../components/AvailabilityEditor';
import RequestDetailDialog from '../components/RequestDetailDialog';
import ClientContactCard from '../components/ClientContactCard';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const STATUS_LABEL = {
  accepted: 'Da svolgere', in_progress: 'In corso', completed: 'Completata',
  reviewed: 'Recensita', cancelled: 'Annullata',
};
const OPEN = ['accepted', 'in_progress'];

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (iso) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
const fmt = (iso) => (iso
  ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : 'Data da definire');
const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
};

const InternalProPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('calendar');
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [selected, setSelected] = useState(dayKey(new Date()));
  const [detailReq, setDetailReq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/center/me/assignments');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento prestazioni');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  const complete = async (requestId) => {
    try {
      await api.post(`/api/center/me/assignments/${requestId}/complete`);
      toast.success('Prestazione segnata come conclusa');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const respond = async (requestId, accept) => {
    try {
      await api.post(`/api/center/me/assignments/${requestId}/respond`, { accept });
      toast.success(accept ? 'Prestazione accettata' : 'Prestazione rifiutata: il Centro è stato avvisato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const byDay = useMemo(() => {
    const map = {};
    items.filter((it) => it.appointment_at).forEach((it) => {
      const k = dayKey(new Date(it.appointment_at));
      (map[k] = map[k] || []).push(it);
    });
    return map;
  }, [items]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = Array.from({ length: offset }, () => null);
    for (let i = 1; i <= days; i += 1) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }),
    [weekStart],
  );

  const noDate = items.filter((it) => !it.appointment_at && OPEN.includes(it.status));
  const pending = items.filter((it) => it.internal_assignment_status === 'pending');
  const todayKey = dayKey(new Date());
  const selectedItems = (byDay[selected] || []).slice().sort((a, b) => a.appointment_at.localeCompare(b.appointment_at));

  const Detail = ({ it, compact }) => (
    <div
      className={`p-3 rounded-xl border border-border/60 bg-card/60 ${it.is_urgent ? 'border-l-4 border-l-red-500' : ''}`}
      data-testid={`assignment-${it.request_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2 font-medium">
          <Clock size={16} weight="duotone" /> {it.appointment_at ? (compact ? hhmm(it.appointment_at) : fmt(it.appointment_at)) : 'Data da definire'}
        </div>
        <div className="flex items-center gap-1.5">
          {it.is_urgent && (
            <Badge className="bg-red-100 text-red-800 border-0 text-xs"><Lightning size={11} weight="fill" className="mr-1" /> URGENTE</Badge>
          )}
          <Badge className="bg-primary/10 text-primary border-0 text-xs">{STATUS_LABEL[it.status] || it.status}</Badge>
        </div>
      </div>
      <div className="text-sm text-foreground/80">{it.service_type || 'Prestazione'}</div>
      {(it.comune || it.region) && (
        <div className="text-xs text-foreground/60 flex items-center gap-1 mt-1">
          <MapPin size={12} /> {[it.comune, it.province, it.region].filter(Boolean).join(' · ')}
        </div>
      )}
      {(it.needs_labels || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {it.needs_labels.slice(0, 4).map((l, i) => <Badge key={i} variant="outline" className="text-xs">{l}</Badge>)}
        </div>
      )}
      {it.description && <p className="text-sm text-foreground/70 line-clamp-2 mt-2">{it.description}</p>}

      {it.contact_locked ? (
        <div className="mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2" data-testid={`locked-${it.request_id}`}>
          <LockSimple size={14} className="mt-0.5" />
          Dati del cliente riservati: si sbloccano quando confermi l'incarico.
        </div>
      ) : (
        <div className="mt-2"><ClientContactCard c={it} testId={`contact-${it.request_id}`} /></div>
      )}
      {it.note && <p className="text-xs text-foreground/60 mt-2">Nota: {it.note}</p>}
      {it.internal_assignment_status === 'pending' ? (
        <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid={`pending-${it.request_id}`}>
          <div className="text-xs text-amber-900 mb-2">
            Il Centro ti ha <strong>pre-assegnato</strong> questa prestazione in un orario fuori dalla
            tua disponibilità. Accetta o rifiuta.
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full h-8" onClick={() => respond(it.request_id, true)} data-testid={`accept-assignment-${it.request_id}`}>
              Accetta
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => respond(it.request_id, false)} data-testid={`decline-assignment-${it.request_id}`}>
              Rifiuta
            </Button>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setDetailReq(it)} data-testid={`detail-${it.request_id}`}>
          <Info size={14} className="mr-1" /> Dettaglio Richiesta
        </Button>
        {it.internal_assignment_status !== 'pending' && OPEN.includes(it.status) && (
          <Button size="sm" className="rounded-full h-8" onClick={() => complete(it.request_id)} data-testid={`complete-${it.request_id}`}>
            <CheckCircle size={14} className="mr-1" /> Segna conclusa
          </Button>
        )}
      </div>
    </div>
  );

  if (loading) return <div className="text-center py-20 text-foreground/60">Caricamento…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="internal-pro-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-semibold text-4xl mb-2">La mia agenda</h1>
          <p className="text-foreground/70 text-sm flex items-center gap-2">
            <Buildings size={16} weight="duotone" className="text-primary" />
            Organico di <strong>{data?.center?.name || 'il tuo Centro'}</strong>
            {data?.center?.phone ? ` · ${data.center.phone}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex rounded-full border border-border overflow-hidden" data-testid="internal-mode-switch">
            <button
              onClick={() => setMode('calendar')} data-testid="internal-mode-calendar"
              className={`px-4 py-1.5 text-sm transition-colors ${mode === 'calendar' ? 'bg-sky-600 text-white' : 'hover:bg-muted/60'}`}
            >
              <CalendarBlank size={14} className="inline mr-1" /> Calendario
            </button>
            <button
              onClick={() => setMode('list')} data-testid="internal-mode-list"
              className={`px-4 py-1.5 text-sm transition-colors ${mode === 'list' ? 'bg-sky-600 text-white' : 'hover:bg-muted/60'}`}
            >
              <ListBullets size={14} className="inline mr-1" /> Elenco
            </button>
            <button
              onClick={() => setMode('availability')} data-testid="internal-mode-availability"
              className={`px-4 py-1.5 text-sm transition-colors ${mode === 'availability' ? 'bg-sky-600 text-white' : 'hover:bg-muted/60'}`}
            >
              <Clock size={14} className="inline mr-1" /> Disponibilità
            </button>
          </div>
          {mode === 'calendar' && (
            <div className="flex rounded-full border border-border overflow-hidden" data-testid="internal-view-switch">
              <button
                onClick={() => { setCursor(new Date(new Date(selected).getFullYear(), new Date(selected).getMonth(), 1)); setView('month'); }}
                data-testid="internal-view-month"
                className={`px-4 py-1.5 text-sm transition-colors ${view === 'month' ? 'bg-sky-600 text-white' : 'hover:bg-muted/60'}`}
              >
                Mese
              </button>
              <button
                onClick={() => { setWeekStart(mondayOf(new Date(selected))); setView('week'); }}
                data-testid="internal-view-week"
                className={`px-4 py-1.5 text-sm transition-colors ${view === 'week' ? 'bg-sky-600 text-white' : 'hover:bg-muted/60'}`}
              >
                Settimana
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
        <Card className="p-4 rounded-2xl" data-testid="internal-stat-total">
          <div className="text-2xl font-semibold">{data?.count || 0}</div>
          <div className="text-xs text-foreground/60">Prestazioni assegnate</div>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="internal-stat-upcoming">
          <div className="text-2xl font-semibold text-primary">{data?.upcoming_count || 0}</div>
          <div className="text-xs text-foreground/60">In programma</div>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="internal-stat-next">
          <div className="text-sm font-semibold">{data?.next ? fmt(data.next.appointment_at) : '—'}</div>
          <div className="text-xs text-foreground/60">Prossimo appuntamento</div>
        </Card>
      </div>

      {pending.length > 0 && mode !== 'availability' && (
        <Card className="p-4 rounded-2xl mb-6 bg-amber-50 border-amber-300" data-testid="internal-pending-banner">
          <div className="text-sm font-semibold mb-1">
            {pending.length} prestazion{pending.length === 1 ? 'e' : 'i'} da confermare
          </div>
          <p className="text-xs text-amber-900">
            Il Centro te le ha pre-assegnate fuori dalla tua disponibilità: trovi i pulsanti Accetta
            e Rifiuta nell'elenco.{' '}
            <button type="button" className="underline font-semibold" onClick={() => setMode('list')} data-testid="internal-pending-open-list">Apri l'elenco</button>
          </p>
        </Card>
      )}

      {noDate.length > 0 && (
        <Card className="p-4 rounded-2xl mb-6 bg-amber-50 border-amber-200" data-testid="internal-nodate">
          <div className="text-sm font-semibold mb-1">{noDate.length} prestazioni in attesa di data</div>
          <p className="text-xs text-amber-900">
            Il cliente deve ancora confermare la data: appena fissata le trovi in calendario.
          </p>
        </Card>
      )}

      {mode === 'availability' ? (
        <AvailabilityEditor />
      ) : items.length === 0 ? (
        <Card className="p-8 text-center rounded-2xl bg-muted/20" data-testid="internal-empty">
          <CalendarBlank size={32} className="mx-auto mb-3 text-foreground/40" weight="duotone" />
          <p className="text-foreground/70">Nessuna prestazione assegnata dal Centro per ora.</p>
        </Card>
      ) : mode === 'list' ? (
        <div className="space-y-3" data-testid="internal-assignments">
          {items.map((it) => <Detail key={it.request_id} it={it} />)}
        </div>
      ) : view === 'month' ? (
        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="rounded-2xl p-5 lg:col-span-3" data-testid="internal-calendar">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 font-heading font-semibold text-lg">
                <CalendarBlank size={20} weight="duotone" /> {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="internal-prev"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
                  <CaretLeft size={14} />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="internal-next"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
                  <CaretRight size={14} />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-xs text-foreground/50 text-center uppercase tracking-wider">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const k = dayKey(d);
                const dayItems = byDay[k] || [];
                const isSel = k === selected;
                const open = dayItems.some((it) => OPEN.includes(it.status));
                return (
                  <button
                    key={k}
                    onClick={() => setSelected(k)}
                    data-testid={`internal-day-${k}`}
                    className={`aspect-square rounded-xl p-1 flex flex-col items-center justify-center transition-all
                      ${isSel ? 'bg-sky-600 text-white shadow-md' : dayItems.length ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-muted/60'}
                      ${k === todayKey && !isSel ? 'ring-1 ring-sky-400' : ''}`}
                  >
                    <span className={`text-sm font-medium ${isSel ? '' : 'text-foreground/80'}`}>{d.getDate()}</span>
                    {dayItems.length > 0 && (
                      <span className={`mt-1 h-1.5 rounded-full ${isSel ? 'bg-white' : open ? 'bg-sky-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min(18, dayItems.length * 6)}px` }} />
                    )}
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="rounded-2xl p-5 lg:col-span-2" data-testid="internal-day-detail">
            <div className="font-heading font-semibold text-lg mb-4">
              {new Date(selected).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            {selectedItems.length === 0 ? (
              <div className="text-sm text-foreground/50" data-testid="internal-empty-day">
                Nessuna prestazione in questa giornata.
              </div>
            ) : (
              <div className="space-y-3">
                {selectedItems.map((it) => <Detail key={it.request_id} it={it} compact />)}
              </div>
            )}
          </Card>
        </div>
      ) : (
        <Card className="rounded-2xl p-5 overflow-x-auto" data-testid="internal-week">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-lg">
              <CalendarBlank size={20} weight="duotone" />
              {weekDays[0].toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} – {weekDays[6].toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
            </div>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="internal-week-prev"
                onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}>
                <CaretLeft size={14} />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="internal-week-next"
                onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>
                <CaretRight size={14} />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2 min-w-[840px]">
            {weekDays.map((d) => {
              const k = dayKey(d);
              const dayItems = (byDay[k] || []).slice().sort((a, b) => a.appointment_at.localeCompare(b.appointment_at));
              return (
                <div key={k} data-testid={`internal-week-col-${k}`}>
                  <div className={`text-xs text-center py-2 rounded-lg mb-2 ${k === todayKey ? 'bg-sky-50 text-sky-700 font-medium' : 'text-foreground/60'}`}>
                    {WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}
                  </div>
                  <div className="space-y-1 min-h-[60px]">
                    {dayItems.map((it) => (
                      <button
                        key={it.request_id}
                        onClick={() => { setSelected(k); setView('month'); }}
                        data-testid={`internal-week-item-${it.request_id}`}
                        className={`w-full text-left rounded-lg px-2 py-1.5 text-[11px] truncate transition-opacity hover:opacity-90
                          ${OPEN.includes(it.status) ? 'bg-sky-600 text-white' : 'bg-emerald-600 text-white'}`}
                      >
                        {hhmm(it.appointment_at)} · {it.service_type || 'Prestazione'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {detailReq && (
        <RequestDetailDialog
          request={detailReq}
          open={!!detailReq}
          onOpenChange={(o) => { if (!o) setDetailReq(null); }}
        />
      )}
    </div>
  );
};

export default InternalProPage;
